import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useLocale } from "../i18n/LocaleContext";
import Lightbox from "./Lightbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Public render order — every drop displays its sections in this exact
// sequence so the brand experience stays perfectly consistent.
const ORDER = ["esterno", "interno", "sx", "dx", "sopra", "sotto", "particolari", "restauro", "video"];

function buildUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/files/")) return `${BACKEND_URL}${path}`;
  return path;
}

/**
 * Cinematic categorized gallery used on the public drop page.
 *
 * Each category is rendered as a horizontal carousel: visitors slide through
 * the photos sideways instead of scrolling vertically. This keeps the drop
 * page short and ordered — one strip per section (Esterno, Interno, Sx, Dx,
 * Sopra, Sotto, Particolari, Restauro, Video).
 *
 * The carousel uses native scroll-snap for smooth swipe on mobile and adds
 * left/right buttons on desktop hover. No external carousel library — just
 * a flex strip with overflow-x.
 */
export default function CategorizedGallery({ drop }) {
  const { t } = useLocale();
  const items = Array.isArray(drop?.gallery_items) ? drop.gallery_items : [];
  const legacy = Array.isArray(drop?.gallery) ? drop.gallery : [];

  // ALL HOOKS MUST BE DECLARED BEFORE ANY EARLY RETURN — React requires
  // hooks to be called in the exact same order on every render.
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const { grouped, sections, flatItems } = useMemo(() => {
    const g = {};
    if (items.length > 0) {
      for (const it of items) {
        const cat = it.category || "particolari";
        if (!g[cat]) g[cat] = [];
        g[cat].push(it);
      }
    } else if (legacy.length > 0) {
      g.particolari = legacy.map((url) => ({ url, kind: "image" }));
    }
    const s = ORDER.filter((k) => g[k]?.length > 0);
    const flat = s.flatMap((k) => g[k]);
    return { grouped: g, sections: s, flatItems: flat };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drop?.id, items.length, legacy.length]);

  const openLightboxAt = (catKey, localIdx) => {
    let offset = 0;
    for (const k of sections) {
      if (k === catKey) break;
      offset += grouped[k].length;
    }
    setLightboxIndex(offset + localIdx);
  };

  if (sections.length === 0) return null;

  return (
    <section
      className="gd-noselect"
      data-testid="categorized-gallery"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-12 pt-32 pb-12">
        <span
          className="text-[10px] uppercase block"
          style={{
            color: "var(--text-muted)",
            letterSpacing: "0.4em",
            fontWeight: 300,
          }}
        >
          {t("drop.gallery")}
        </span>
      </div>

      {sections.map((catKey) => (
        <CategorySection
          key={catKey}
          catKey={catKey}
          label={t(`drop.categories.${catKey}`)}
          items={grouped[catKey]}
          onOpen={openLightboxAt}
        />
      ))}

      <Lightbox
        items={flatItems}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() =>
          setLightboxIndex((i) =>
            i === null ? null : (i - 1 + flatItems.length) % flatItems.length,
          )
        }
        onNext={() =>
          setLightboxIndex((i) =>
            i === null ? null : (i + 1) % flatItems.length,
          )
        }
      />
    </section>
  );
}

/**
 * Apple-style gallery section: tiny mosaic grid (3 cols mobile, 4 desktop),
 * generous spacing, hairline category label. Tap any thumbnail → lightbox.
 */
function CategorySection({ catKey, label, items, onOpen }) {
  return (
    <div
      className="pb-24"
      data-testid={`gallery-section-${catKey}`}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-12 mb-8">
        <h3
          className="text-2xl sm:text-3xl font-light"
          style={{ letterSpacing: "-0.005em", color: "var(--text-primary)" }}
        >
          {label}
          <span
            className="ms-3 text-xs font-mono tabular-nums"
            style={{ color: "var(--text-muted)" }}
          >
            {items.length.toString().padStart(2, "0")}
          </span>
        </h3>
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
          {items.map((item, i) => (
            <motion.button
              key={`${item.url}-${i}`}
              type="button"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "0px 0px -50px 0px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="relative overflow-hidden bg-black group"
              style={{ aspectRatio: "1/1" }}
              onClick={() => onOpen(catKey, i)}
              data-testid={`gallery-item-${catKey}-${i}`}
              aria-label={`${label} ${i + 1}`}
            >
              {item.kind === "video" ? (
                <>
                  {item.poster_url ? (
                    /* Custom cover image set by the admin — preferred path.
                       Apple-style: a still photo with a soft play glyph,
                       no autoplay flicker, instant first paint. */
                    <img
                      src={buildUrl(item.poster_url)}
                      alt=""
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      loading="lazy"
                      draggable={false}
                    />
                  ) : (
                    <video
                      src={`${buildUrl(item.url)}#t=0.1`}
                      className="w-full h-full object-cover pointer-events-none"
                      muted
                      playsInline
                      preload="metadata"
                      onLoadedMetadata={(e) => {
                        // Force a frame to be decoded so the thumbnail is
                        // not black. iOS Safari and some Chromium builds
                        // ignore the `#t=` fragment for chunked sources,
                        // so we explicitly seek past the start.
                        try {
                          if (e.currentTarget.currentTime < 0.05) {
                            e.currentTarget.currentTime = 0.1;
                          }
                        } catch {
                          /* best-effort poster generation */
                        }
                      }}
                    />
                  )}
                  {/* Subtle play glyph so the user immediately understands
                      this tile is a video, not a broken image. */}
                  <span
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    aria-hidden="true"
                  >
                    <span
                      className="flex items-center justify-center rounded-full"
                      style={{
                        width: 44,
                        height: 44,
                        background: "rgba(0,0,0,0.55)",
                        backdropFilter: "blur(6px)",
                        border: "1px solid rgba(255,255,255,0.25)",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        style={{ marginLeft: 2 }}
                      >
                        <path
                          d="M2.5 1.5L12 7L2.5 12.5V1.5Z"
                          fill="rgba(255,255,255,0.95)"
                        />
                      </svg>
                    </span>
                  </span>
                  <span
                    className="absolute bottom-2 right-2 text-[9px] uppercase px-2 py-0.5"
                    style={{
                      color: "rgba(255,255,255,0.9)",
                      background: "rgba(0,0,0,0.55)",
                      letterSpacing: "0.2em",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    Video
                  </span>
                </>
              ) : (
                <img
                  src={buildUrl(item.url)}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  loading="lazy"
                  draggable={false}
                />
              )}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}
